<template>
  <div class="card card-white">
    <BaseDataTable
      :columns="columns"
      :entries="isLoading ? [] : entries"
      :default-page-length-options="[15, 50, 100]"
      :default-page-length="15"
      :loading="isLoading"
      class="recruiter-bills-table"
    >
      <template #Identifier="{ row }">
        <div>{{ row.id }}</div>
        <a v-if="row.supplierInvoiceNumber" :href="row.invoicePdf" target="_blank">
          {{ row.supplierInvoiceNumber }}
        </a>
      </template>
      <template #Jobs="{ row }">
        <div>{{ row.company }}</div>
        <div>
          <a v-if="row.jobExists" :href="row.jobLink">{{ row.jobName }}</a>
          <em v-else>{{ $t("The job doesn't exist") }}</em>
        </div>
      </template>
      <template #Freelancer="{ row }">
        <div>
          <a v-if="row.freelancerProfileUrl && row.freelancerName" :href="row.freelancerProfileUrl">
            {{ row.freelancerName }}
          </a>
          <div v-else-if="row.freelancerName">
            {{ row.freelancerName }}
          </div>
          <em v-else>{{ $t("The freelancer doesn't exist") }}</em>
        </div>
      </template>
      <template #Status="{ row }">
        <WdsTooltip v-if="row.billStatusHelperText" :text="row.billStatusHelperText">
          <WdsLabel v-if="row.billStatus" sm v-bind="{ [row.billStatusType]: true }">
            {{ row.billStatus }}
          </WdsLabel>
        </WdsTooltip>
        <WdsLabel v-else-if="row.billStatus" sm v-bind="{ [row.billStatusType]: true }">
          {{ row.billStatus }}
        </WdsLabel>
      </template>
      <template #BillTime="{ row }">
        {{ row.billTime > 0 ? row.billTime : '-' }}
      </template>
    </BaseDataTable>
  </div>
</template>

<script setup>
import { defineProps, ref, onMounted, onUnmounted, markRaw } from 'vue'
import moment from 'moment'
import { WdsLabel, WdsTooltip } from '@worksome/wds-components'
import BaseDataTable from '@/components/BaseDataTable'
import { TdText } from '@/components/BaseDataTable/Cells'

const props = defineProps({
  bills: {
    type: Array,
    default: () => [],
  },
  preferredRateType: {
    type: String,
    required: true,
  },
})

const DATE_FORMAT = 'MMM D, YYYY'

const entries = props.bills.map((bill) => ({
  ...bill,
  date: moment(bill.date).format(DATE_FORMAT),
  dueDate: moment(bill.dueDate).format(DATE_FORMAT),
}))

const isLoading = ref(true)

function finishLoading() {
  isLoading.value = false
}

onMounted(() => window.addEventListener('load', finishLoading))
onUnmounted(() => window.removeEventListener('load', finishLoading))

const columns = [
  {
    key: 'id',
    slot: 'Identifier',
    title: $t('No.'),
    orderAble: true,
    type: Number,
  },
  {
    key: 'date',
    title: $t('Date'),
    orderAble: true,
    convert: (record) => new Date(record.date),
  },
  {
    key: 'jobs',
    slot: 'Jobs',
    title: $t('Jobs'),
    orderAble: true,
    sort: 'jobName',
  },
  {
    key: 'freelancer',
    slot: 'Freelancer',
    title: $t('Freelancer'),
    orderAble: true,
    sort: 'freelancerName',
  },
  {
    key: 'status',
    slot: 'Status',
    title: $t('Status'),
  },
  {
    key: 'billTime',
    slot: 'BillTime',
    title: props.preferredRateType,
    orderAble: true,
    class: 'tw-text-center',
    type: Number,
  },
  {
    key: 'total',
    title: $t('Total'),
    orderAble: true,
    renderUsing: {
      component: markRaw(TdText),
      componentProps: (record) => ({
        html: `${record.currency} ${record.total}`,
      }),
    },
  },
  {
    key: 'recruiterFee',
    title: $t('Recr. fee'),
    orderAble: true,
    renderUsing: {
      component: markRaw(TdText),
      componentProps: (record) => ({
        html: `${record.currency} ${record.recruiterFee}`,
      }),
    },
  },
  {
    key: 'dueDate',
    title: $t('Due'),
    orderAble: true,
    class: 'tw-text-right',
    convert: (record) => new Date(record.dueDate),
  },
]
</script>

<style>
.recruiter-bills-table {
  overflow-x: visible;
}
</style>
